import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api, { apiError } from "@/lib/api";
import { PageHeader, LoadingSkeleton } from "@/components/Layout";
import Heatmap from "@/components/Heatmap";
import OnboardingWizard from "@/components/OnboardingWizard";
import { SeverityBadge } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { AlertTriangle, TrendingUp, ShieldAlert, CheckCircle2, ArrowUpRight, Gauge, DollarSign } from "lucide-react";
import { formatDateTime } from "@/lib/dates";
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  RadialBarChart, RadialBar, PolarAngleAxis, PolarGrid, PolarRadiusAxis,
  RadarChart, Radar, Legend, CartesianGrid,
} from "recharts";

const SEV_COLORS = { Low: "#10B981", Moderate: "#F59E0B", High: "#EA580C", Critical: "#E11D48" };
const KPI_TONES = {
  slate:  { border: "border-slate-200/80",  ring: "hover:border-slate-400 hover:ring-slate-100",  accent: "text-slate-500",  bar: "bg-slate-400"  },
  rose:   { border: "border-rose-200/70",   ring: "hover:border-rose-400 hover:ring-rose-100",    accent: "text-rose-600",   bar: "bg-rose-500"   },
  orange: { border: "border-orange-200/70", ring: "hover:border-orange-400 hover:ring-orange-100",accent: "text-orange-600", bar: "bg-orange-500" },
  amber:  { border: "border-amber-200/70",  ring: "hover:border-amber-400 hover:ring-amber-100",  accent: "text-amber-700",  bar: "bg-amber-500"  },
};

export default function Dashboard() {
  const [heat, setHeat] = useState(null);
  const [risks, setRisks] = useState([]);
  const [audit, setAudit] = useState([]);
  const [userMap, setUserMap] = useState({});
  const [appetite, setAppetite] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user, can } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [h, r, ap] = await Promise.all([
          api.get("/dashboard/heatmap"),
          api.get("/risks"),
          api.get("/appetite/summary").catch(() => ({ data: null })),
        ]);
        setHeat(h.data); setRisks(r.data); setAppetite(ap.data);
        if (can("audit.read")) {
          try {
            const [a, u] = await Promise.all([api.get("/audit?limit=8"), api.get("/users")]);
            setAudit(a.data);
            const m = {};
            for (const uu of (u.data || [])) m[(uu.email || "").toLowerCase()] = uu.name || uu.email;
            setUserMap(m);
          } catch {}
        }
      } catch (e) { toast.error(apiError(e)); }
      finally { setLoading(false); }
    })();
  }, [can]);

  const tally = heat?.tally || {};
  const total = heat?.total || 0;
  const breaches = heat?.breaches || 0;

  const severityData = useMemo(() => (
    ["Critical", "High", "Moderate", "Low"]
      .map((s) => ({ name: s, value: tally[s] || 0, color: SEV_COLORS[s] }))
      .filter((d) => d.value > 0)
  ), [tally]);

  const categoryData = useMemo(() => {
    const m = {};
    for (const r of risks) m[r.category || "Uncategorised"] = (m[r.category || "Uncategorised"] || 0) + 1;
    return Object.entries(m).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 8);
  }, [risks]);

  const statusData = useMemo(() => {
    const m = {};
    for (const r of risks) m[r.status || "Open"] = (m[r.status || "Open"] || 0) + 1;
    return Object.entries(m).map(([name, value], i) => ({
      name, value,
      fill: ["#0EA5E9", "#22D3EE", "#10B981", "#F59E0B", "#94A3B8"][i % 5],
    }));
  }, [risks]);

  // Current vs Target: average score per category. Target score is the
  // residual the Risk Owner is aiming for once treatment is complete, so this
  // chart shows the *delta* the register is committing to deliver.
  const currentVsTarget = useMemo(() => {
    const bucket = {};
    for (const r of risks) {
      if (r.status === "Closed") continue;
      const cat = r.category || "Uncategorised";
      const cur = Number(r.current_score || 0);
      const tgt = Number(((r.target?.likelihood || 1) * (r.target?.impact || 1)) || 0);
      if (!bucket[cat]) bucket[cat] = { name: cat, _cur: 0, _tgt: 0, count: 0 };
      bucket[cat]._cur += cur;
      bucket[cat]._tgt += tgt;
      bucket[cat].count += 1;
    }
    return Object.values(bucket)
      .map((b) => ({
        name: b.name,
        current: Math.round((b._cur / b.count) * 10) / 10,
        target: Math.round((b._tgt / b.count) * 10) / 10,
        count: b.count,
        delta: Math.round(((b._cur - b._tgt) / b.count) * 10) / 10,
      }))
      .sort((a, b) => b.current - a.current)
      .slice(0, 10);
  }, [risks]);

  const breachPct = total ? Math.round((breaches / total) * 100) : 0;
  const criticalPct = total ? Math.round(((tally.Critical || 0) / total) * 100) : 0;

  const kpis = [
    { label: "Total risks",        value: total,             hint: "in scope",                    tone: "slate",  icon: ShieldAlert,     testid: "kpi-total",     to: "/risks" },
    { label: "Critical",           value: tally.Critical || 0, hint: `${criticalPct}% of register`, tone: "rose",   icon: AlertTriangle,   testid: "kpi-critical",  to: "/risks?severity=Critical" },
    { label: "High",               value: tally.High || 0,   hint: "priority",                    tone: "orange", icon: TrendingUp,      testid: "kpi-high",      to: "/risks?severity=High" },
    { label: "Appetite breaches",  value: breaches,          hint: `${breachPct}% breach rate`,   tone: "amber",  icon: CheckCircle2,    testid: "kpi-breaches",  to: "/risks?breach=1" },
  ];

  if (loading) return <div className="p-8"><LoadingSkeleton rows={5} /></div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader
        kicker={`Welcome back, ${user?.name?.split(" ")[0] || "there"}`}
        title="Risk Overview"
        description="KPI tiles, 5×5 heatmap and charted views of where your risk sits today."
        actions={<Button className="bg-[#0A1329] hover:bg-[#162347] text-white" onClick={() => nav("/risks")} data-testid="dashboard-open-register">Open Risk Register →</Button>}
      />

      {/* First-run 10-minute walk-through. Component self-hides once every step
          is complete, or if an admin has dismissed it. */}
      <OnboardingWizard variant="card" initialOpen />

      {/* KPIs — clickable, drill into filtered register */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {kpis.map((k) => {
          const t = KPI_TONES[k.tone] || KPI_TONES.slate;
          return (
            <button
              key={k.label}
              type="button"
              onClick={() => nav(k.to)}
              className={`text-left group relative overflow-hidden rounded-xl border ${t.border} bg-white p-5 shadow-sm transition-all duration-200 ${t.ring} hover:shadow-md hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500`}
              data-testid={k.testid}
              aria-label={`${k.label} — open register filtered by ${k.label}`}
            >
              <span className={`absolute left-0 top-0 h-full w-1 ${t.bar}`} aria-hidden />
              <div className="flex items-start justify-between">
                <div>
                  <div className={`text-[10px] uppercase tracking-widest font-mono ${t.accent} font-semibold`}>{k.label}</div>
                  <div className="font-display font-extrabold text-4xl text-slate-900 tabular-nums mt-1">{k.value}</div>
                  <div className="text-xs text-slate-500 mt-1">{k.hint}</div>
                </div>
                <k.icon className="h-5 w-5 text-slate-400" />
              </div>
              <div className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 group-hover:text-slate-900 transition-colors">
                View in register <ArrowUpRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </div>
            </button>
          );
        })}
      </div>

      {/* Charts row — moved above heatmap per governance-overview flow */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6" data-testid="dashboard-charts">
        <Card className="lg:col-span-4 p-6 border-slate-200/80" data-testid="chart-severity">
          <div className="text-[10px] uppercase tracking-widest font-mono text-cyan-700 font-semibold">Severity mix</div>
          <h3 className="font-display font-bold text-lg text-slate-900 mb-3">Risks by severity</h3>
          {severityData.length ? (
            <div className="h-56 relative">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={severityData} innerRadius={54} outerRadius={82} paddingAngle={3} dataKey="value" stroke="#FFFFFF" strokeWidth={2}>
                    {severityData.map((e) => <Cell key={e.name} fill={e.color} />)}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <div className="text-3xl font-bold text-slate-900 tabular-nums">{total}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">risks</div>
              </div>
            </div>
          ) : <EmptyChart label="No risks yet" />}
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 justify-center text-xs">
            {severityData.map((s) => (
              <div key={s.name} className="flex items-center gap-1.5 text-slate-600">
                <span className="h-2 w-2 rounded-full" style={{ background: s.color }} /> {s.name} · <span className="font-mono tabular-nums font-semibold">{s.value}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card className="lg:col-span-5 p-6 border-slate-200/80" data-testid="chart-category">
          <div className="text-[10px] uppercase tracking-widest font-mono text-cyan-700 font-semibold">Distribution</div>
          <h3 className="font-display font-bold text-lg text-slate-900 mb-3">Risks by category</h3>
          {categoryData.length ? (
            <div className="h-64">
              <ResponsiveContainer>
                <BarChart data={categoryData} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: "#64748B" }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "#334155" }} width={112} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} cursor={{ fill: "rgba(14,116,144,0.06)" }} />
                  <Bar dataKey="value" fill="#0E7490" radius={[0, 6, 6, 0]} maxBarSize={24} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : <EmptyChart label="No categories yet" />}
        </Card>

        <Card className="lg:col-span-3 p-6 border-slate-200/80" data-testid="chart-status">
          <div className="text-[10px] uppercase tracking-widest font-mono text-cyan-700 font-semibold">Lifecycle</div>
          <h3 className="font-display font-bold text-lg text-slate-900 mb-3">Status</h3>
          {statusData.length ? (
            <div className="h-56">
              <ResponsiveContainer>
                <RadialBarChart innerRadius="40%" outerRadius="100%" data={statusData} startAngle={90} endAngle={-270}>
                  <PolarAngleAxis type="number" domain={[0, Math.max(...statusData.map((d) => d.value)) || 1]} tick={false} />
                  <RadialBar background={{ fill: "#F1F5F9" }} dataKey="value" cornerRadius={6} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
                </RadialBarChart>
              </ResponsiveContainer>
            </div>
          ) : <EmptyChart label="No status data" />}
          <div className="mt-3 space-y-1 text-xs">
            {statusData.map((s) => (
              <div key={s.name} className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-slate-600">
                  <span className="h-2 w-2 rounded-full" style={{ background: s.fill }} /> {s.name}
                </span>
                <span className="font-mono tabular-nums font-semibold text-slate-800">{s.value}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Heatmap + Top risks */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <Card className="lg:col-span-7 p-6 border-slate-200/80">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest font-mono text-cyan-700 font-semibold">Heatmap</div>
              <h2 className="font-display font-bold text-xl text-slate-900">Risk Heatmap · 5×5</h2>
            </div>
            <div className="text-xs font-mono text-slate-500">Likelihood × Impact</div>
          </div>
          <Heatmap grid={heat?.grid} onCellClick={(key, meta) => nav(`/risks?likelihood=${meta.likelihood}&impact=${meta.impact}`)} />
        </Card>

        <Card className="lg:col-span-5 p-6 border-slate-200/80">
          <div className="text-[10px] uppercase tracking-widest font-mono text-cyan-700 font-semibold">Top of the list</div>
          <h2 className="font-display font-bold text-xl text-slate-900 mb-4">Top risks by score</h2>
          <div className="space-y-2" data-testid="top-risks">
            {risks.slice(0, 6).map((r) => (
              <button key={r.id} onClick={() => nav(`/risks/${r.id}`)} className="w-full text-left flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-cyan-400 hover:bg-cyan-50/40 transition-colors" data-testid={`toprisk-${r.code}`}>
                <div className="font-mono font-bold text-slate-800 tabular-nums w-16">{r.code}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">{r.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{r.category} · Score <span className="font-mono tabular-nums font-semibold">{r.current_score}</span></div>
                </div>
                <SeverityBadge severity={r.current_severity} />
              </button>
            ))}
            {risks.length === 0 && <div className="text-sm text-slate-500 text-center py-6">No risks yet.</div>}
          </div>
        </Card>
      </div>

      {/* Current vs Target — bar chart (left) + radar (right). Both tell the
          same story (residual we have today vs the residual we're aiming for)
          but Boards tend to read one or the other more naturally. */}
      {currentVsTarget.length > 0 && (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="dashboard-current-vs-target">
          <Card className="lg:col-span-7 p-6 border-slate-200/80">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-mono text-cyan-700 font-semibold">Treatment plan</div>
                <h2 className="font-display font-bold text-xl text-slate-900">Existing vs planned risk</h2>
                <div className="text-xs text-slate-500 mt-0.5">Average 5×5 score per category · open risks only</div>
              </div>
              <div className="hidden md:flex items-center gap-3 text-[11px] font-mono text-slate-500">
                <LegendDot color="#E11D48" label="Existing" />
                <LegendDot color="#22D3EE" label="Planned" />
              </div>
            </div>
            <div className="h-72" data-testid="existing-vs-planned-bar">
              <ResponsiveContainer>
                <BarChart data={currentVsTarget} margin={{ top: 8, right: 16, left: 0, bottom: 20 }} barCategoryGap="22%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#475569" }} interval={0} angle={-14} textAnchor="end" height={46} />
                  <YAxis domain={[0, 25]} tick={{ fontSize: 11, fill: "#64748B" }} />
                  <Tooltip
                    contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }}
                    formatter={(v, k) => [`${v}`, k === "current" ? "Existing" : "Planned"]}
                  />
                  <Bar dataKey="current" name="Existing" fill="#E11D48" radius={[4, 4, 0, 0]} maxBarSize={38} />
                  <Bar dataKey="target"  name="Planned"  fill="#22D3EE" radius={[4, 4, 0, 0]} maxBarSize={38} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card className="lg:col-span-5 p-6 border-slate-200/80">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-mono text-cyan-700 font-semibold">Shape</div>
                <h2 className="font-display font-bold text-xl text-slate-900">Risk profile</h2>
                <div className="text-xs text-slate-500 mt-0.5">The smaller the cyan shape, the better the plan</div>
              </div>
            </div>
            <div className="h-72" data-testid="existing-vs-planned-radar">
              <ResponsiveContainer>
                <RadarChart data={currentVsTarget} outerRadius="78%">
                  <PolarGrid stroke="#E2E8F0" />
                  <PolarAngleAxis dataKey="name" tick={{ fontSize: 10, fill: "#475569" }} />
                  <PolarRadiusAxis angle={90} domain={[0, 25]} tick={{ fontSize: 9, fill: "#94A3B8" }} tickCount={6} />
                  <Radar name="Existing" dataKey="current" stroke="#E11D48" fill="#E11D48" fillOpacity={0.28} strokeWidth={2} />
                  <Radar name="Planned"  dataKey="target"  stroke="#22D3EE" fill="#22D3EE" fillOpacity={0.28} strokeWidth={2} />
                  <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>
      )}

      {appetite && (appetite.has_policy || appetite.total_breaches > 0) && (
        <Card className="mt-6 p-6 border-slate-200/80" data-testid="dashboard-appetite">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest font-mono text-cyan-700 font-semibold">Board policy</div>
              <h2 className="font-display font-bold text-xl text-slate-900 flex items-center gap-2">
                <Gauge className="h-4 w-4 text-cyan-700" /> Risk appetite posture
              </h2>
              <div className="text-xs text-slate-500 mt-0.5">
                v{appetite.version} · overall threshold <span className="font-mono font-semibold text-slate-800">{appetite.threshold_severity}</span>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => nav("/appetite")} data-testid="appetite-open">Open appetite →</Button>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <button type="button" onClick={() => nav("/risks?breach=1")} className="text-left rounded-lg border border-rose-200 bg-rose-50/40 p-3 hover:border-rose-400 transition-colors" data-testid="appetite-tile-breaches">
              <div className="text-[10px] uppercase tracking-widest font-mono text-rose-700 font-semibold">Breaches now</div>
              <div className="font-display font-extrabold text-3xl text-slate-900 tabular-nums">{appetite.total_breaches}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">of {appetite.total_risks} open risks</div>
            </button>
            <div className="rounded-lg border border-slate-200 p-3">
              <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 font-semibold">Exposure (single-risk cap)</div>
              <div className="font-display font-extrabold text-2xl text-slate-900 tabular-nums flex items-center gap-1"><DollarSign className="h-4 w-4 text-slate-400" />{(appetite.max_single_breach || 0).toLocaleString()}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">largest single breach vs cap</div>
            </div>
            <div className="rounded-lg border border-slate-200 p-3">
              <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 font-semibold">Portfolio exposure</div>
              <div className={`font-display font-extrabold text-2xl tabular-nums flex items-center gap-1 ${appetite.portfolio_breach ? "text-rose-700" : "text-slate-900"}`}><DollarSign className="h-4 w-4 text-slate-400" />{(appetite.portfolio_sum || 0).toLocaleString()}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">cap: ${(appetite.portfolio_cap || 0).toLocaleString()}</div>
            </div>
            <div className="rounded-lg border border-slate-200 p-3">
              <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 font-semibold">Breaching categories</div>
              <div className="font-display font-extrabold text-3xl text-slate-900 tabular-nums">{appetite.categories_breaching}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">of {appetite.categories_total} tracked</div>
            </div>
          </div>
        </Card>
      )}

      {audit.length > 0 && (
        <Card className="mt-6 p-6 border-slate-200/80">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest font-mono text-cyan-700 font-semibold">Immutable · hash-chained</div>
              <h2 className="font-display font-bold text-xl text-slate-900">Recent activity</h2>
            </div>
            <Button variant="ghost" size="sm" onClick={() => nav("/audit")}>View all →</Button>
          </div>
          <div className="space-y-1.5 text-sm">
            {audit.map((e) => {
              const email = (e.actor_email || "").toLowerCase();
              const name = userMap[email] || e.actor_email || "Unknown";
              return (
                <div key={e.id} className="flex items-center gap-3 py-1.5 border-b border-slate-100 last:border-0" data-testid={`recent-activity-${e.seq}`}>
                  <span className="font-mono text-[10px] text-slate-400 tabular-nums w-10">#{e.seq}</span>
                  <span className="font-mono text-xs font-semibold text-cyan-800 bg-cyan-50 border border-cyan-200 rounded px-1.5 py-0.5">{e.action}</span>
                  <span className="text-slate-700 text-xs truncate flex-1 font-medium" title={e.actor_email}>{name}</span>
                  <span className="text-[10px] text-slate-400 font-mono">{formatDateTime(e.created_at)}</span>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="h-2.5 w-2.5 rounded-full" style={{ background: color }} />
      {label}
    </span>
  );
}


function EmptyChart({ label }) {
  return <div className="h-56 flex items-center justify-center text-sm text-slate-400 border border-dashed border-slate-200 rounded-md">{label}</div>;
}
